import React, { useEffect, useMemo, useState } from "react";
import { Alert, Flex, Typography, Avatar, Button, Popover, Tag, Tooltip } from "antd";
import { GlassCard } from "../../../../shared/components";
import {
    ClockCircleOutlined,
    EditOutlined,
    InfoCircleOutlined,
    LinkOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { useAvatarPalette } from "../../../../shared/styles";
import { getAvatarColor, getInitials, getInitialsFromName } from "../../../../shared/utils";
import { AdminRole, TunerRole } from "../../../../models/user";

type ProductMapping = {
    productId?: number;
    remoteProductId?: string;
};

type OrderProductSummary = {
    id: number;
    name?: string;
    code?: string;
};

export type ExternalPushStatus = {
    serviceProviderId: number;
    status: string;
    attemptsMade: number | null;
    maxAttempts: number | null;
    canRetry: boolean;
    lastError: string | null;
    externalOrderId: string | null;
    ackedAt: string | null;
    unmappedProductIds: number[];
};

export interface OrderAssignmentCardProps {
    assignedUserId?: number;
    assignedServiceProviderId?: number;
    assignedGroupId?: number;
    assignmentStatus?: "pending" | "assigned";
    createdAt?: string | Date;
    usersData?: any;
    serviceProvidersData?: any;
    groupsData?: any;
    orderProducts?: OrderProductSummary[];
    externalPushStatus?: ExternalPushStatus | null;
    onOpenModal: () => void;
    onOpenProductMapping?: () => void;
    onRetryExternalPush?: () => void;
    isRetryingExternalPush?: boolean;
    isAdmin: boolean;
    _t: (key: string) => string;
}

const statusTagColor = (status: string): string => {
    switch (status) {
        case "synced":
            return "success";
        case "failed":
            return "error";
        case "partial":
            return "warning";
        case "active":
        case "retrying":
        case "queued":
            return "processing";
        default:
            return "default";
    }
};

const statusLabelKey = (status: string): string => {
    switch (status) {
        case "synced":
            return "orders.externalPush.statusSynced";
        case "queued":
            return "orders.externalPush.statusQueued";
        case "active":
            return "orders.externalPush.statusActive";
        case "retrying":
            return "orders.externalPush.statusRetrying";
        case "failed":
            return "orders.externalPush.statusFailed";
        case "partial":
            return "orders.externalPush.statusPartial";
        case "not_started":
        default:
            return "orders.externalPush.statusNotStarted";
    }
};

export const OrderAssignmentCard: React.FC<OrderAssignmentCardProps> = ({
    assignedUserId,
    assignedServiceProviderId,
    assignedGroupId,
    assignmentStatus,
    createdAt,
    usersData,
    serviceProvidersData,
    groupsData,
    orderProducts,
    externalPushStatus,
    onOpenModal,
    onOpenProductMapping,
    onRetryExternalPush,
    isRetryingExternalPush = false,
    isAdmin,
    _t,
}) => {
    const avatarPalette = useAvatarPalette();
    const [elapsedTime, setElapsedTime] = useState<string>("00:00");

    const tuners = useMemo(() => {
        return (
            usersData?.data?.filter(
                (user: any) => user.role?.id === TunerRole.id || user.role?.id === AdminRole.id
            ) || []
        );
    }, [usersData]);

    useEffect(() => {
        if (!createdAt || assignmentStatus !== "pending") {
            setElapsedTime("00:00");
            return;
        }
        const updateTimer = () => {};
        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [createdAt, assignmentStatus]);

    const getAssignedEntity = () => {
        if (assignedUserId) {
            const user = tuners.find((u: any) => u.id === assignedUserId);
            if (user) {
                const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
                return {
                    type: "tuner" as const,
                    data: user,
                    name: name || user.email || `User ${user.id}`,
                    initials: getInitials(user.firstName, user.lastName),
                    color: getAvatarColor(user.id, avatarPalette),
                    roleName: user.role?.name,
                };
            }
        }
        if (assignedGroupId) {
            const group = groupsData?.data?.find((g: any) => g.id === assignedGroupId);
            if (group) {
                return {
                    type: "group" as const,
                    data: group,
                    name: group.name,
                    initials: getInitialsFromName(group.name),
                    color: getAvatarColor(group.id, avatarPalette),
                    memberCount: group.users?.length || 0,
                    members: group.users || [],
                };
            }
        }
        if (assignedServiceProviderId) {
            const provider = serviceProvidersData?.data?.find(
                (p: any) => p.id === assignedServiceProviderId
            );
            if (provider) {
                return {
                    type: "provider" as const,
                    data: provider,
                    name: provider.name,
                    initials: getInitialsFromName(provider.name),
                    color: getAvatarColor(provider.id, avatarPalette),
                };
            }
        }
        return null;
    };

    const assignedEntity = getAssignedEntity();
    const isAssigned = assignmentStatus === "assigned" && assignedEntity;

    const unmappedProducts = useMemo(() => {
        const products = orderProducts ?? [];
        const productsById = new Map(products.map((product) => [product.id, product]));
        if (externalPushStatus?.unmappedProductIds?.length) {
            return externalPushStatus.unmappedProductIds.map((productId) => {
                const product = productsById.get(productId);
                return {
                    id: productId,
                    name: product?.name,
                    code: product?.code,
                };
            });
        }
        if (!assignedEntity || assignedEntity.type !== "provider") {
            return [];
        }
        if (products.length === 0) {
            return [];
        }
        const mappings: ProductMapping[] = assignedEntity.data?.productMappings ?? [];
        const mappedIds = new Set(
            mappings
                .filter((m) => m.productId != null && String(m.remoteProductId ?? "").trim() !== "")
                .map((m) => m.productId as number)
        );
        return products
            .filter((product) => !mappedIds.has(product.id))
            .map((product) => ({
                id: product.id,
                name: product.name,
                code: product.code,
            }));
    }, [assignedEntity, orderProducts, externalPushStatus]);

    const unmappedProductCount = unmappedProducts.length;
    const showRetry = !!externalPushStatus?.canRetry && unmappedProductCount === 0;

    const unmappedProductsPopoverContent = (
        <Flex vertical gap={6} className="max-w-xs">
            <ul className="m-0 list-disc pl-4">
                {unmappedProducts.map((product) => (
                    <li key={product.id}>
                        <Typography.Text className="text-sm">
                            {product.name?.trim() || `#${product.id}`}
                            {product.code ? (
                                <Typography.Text type="secondary" className="text-xs ml-1">
                                    ({product.code})
                                </Typography.Text>
                            ) : null}
                        </Typography.Text>
                    </li>
                ))}
            </ul>
        </Flex>
    );

    if (!isAdmin) {
        return null;
    }

    if (isAssigned) {
        return (
            <GlassCard className="mb-6 mt-5 shadow-lg">
                <Flex gap={12} align="start">
                    <Avatar
                        size={48}
                        shape="square"
                        className="font-semibold"
                        style={{ backgroundColor: assignedEntity.color }}
                    >
                        {assignedEntity.initials}
                    </Avatar>
                    <Flex vertical gap={4} className="flex-1">
                        <Typography.Text strong className="text-base">
                            {assignedEntity.name}
                        </Typography.Text>
                        {assignedEntity.type === "group" && (
                            <Flex gap={4} align="center">
                                <Avatar.Group size="small">
                                    {assignedEntity.members.slice(0, 3).map((user: any, i: number) => (
                                        <Tooltip
                                            key={i}
                                            title={`${user.firstName || ""} ${user.lastName || ""}`.trim()}
                                        >
                                            <Avatar
                                                size="small"
                                                shape="square"
                                                style={{
                                                    backgroundColor: getAvatarColor(
                                                        user.id,
                                                        avatarPalette
                                                    ),
                                                }}
                                            >
                                                {getInitials(user.firstName, user.lastName)}
                                            </Avatar>
                                        </Tooltip>
                                    ))}
                                </Avatar.Group>
                                <Typography.Text className="text-xs">
                                    {assignedEntity.memberCount}{" "}
                                    {assignedEntity.memberCount === 1
                                        ? _t("user-groups.fields.users")?.slice(0, -1)
                                        : _t("user-groups.fields.users")}
                                </Typography.Text>
                            </Flex>
                        )}
                        {assignedEntity.type === "tuner" && assignedEntity.roleName && (
                            <Typography.Text type="secondary" className="text-xs">
                                {assignedEntity.roleName}
                            </Typography.Text>
                        )}
                    </Flex>
                    <Flex align="end">
                        <Button
                            icon={<EditOutlined />}
                            onClick={onOpenModal}
                            type="text"
                            className="hover:text-gray-900"
                        />
                    </Flex>
                </Flex>

                {assignedEntity.type === "provider" && (
                    <Flex vertical gap={8} className="mt-4">
                        <Flex gap={8} align="center" wrap="wrap">
                            <Tag color={statusTagColor(externalPushStatus?.status ?? "not_started")}>
                                {_t(statusLabelKey(externalPushStatus?.status ?? "not_started"))}
                            </Tag>
                            {externalPushStatus?.attemptsMade != null &&
                                externalPushStatus?.maxAttempts != null &&
                                externalPushStatus.status !== "synced" && (
                                    <Typography.Text type="secondary" className="text-xs">
                                        {_t("orders.externalPush.attempts")
                                            .replace(
                                                "{current}",
                                                String(externalPushStatus.attemptsMade)
                                            )
                                            .replace(
                                                "{max}",
                                                String(externalPushStatus.maxAttempts)
                                            )}
                                    </Typography.Text>
                                )}
                        </Flex>
                        {unmappedProductCount > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                message={
                                    <Flex gap={8} align="center" wrap="wrap">
                                        <span>{_t("orders.externalPush.missingMapping")}</span>
                                        <Popover
                                            content={unmappedProductsPopoverContent}
                                            trigger="click"
                                            placement="bottomLeft"
                                        >
                                            <Button
                                                type="link"
                                                size="small"
                                                className="h-auto p-0"
                                            >
                                                {_t(
                                                    "orders.externalPush.missingMappingDetails"
                                                ).replace("{count}", String(unmappedProductCount))}
                                            </Button>
                                        </Popover>
                                    </Flex>
                                }
                                action={
                                    <Button
                                        size="small"
                                        type="primary"
                                        icon={<LinkOutlined />}
                                        onClick={onOpenProductMapping}
                                    >
                                        {_t("orders.externalPush.mapProducts")}
                                    </Button>
                                }
                            />
                        )}
                        {showRetry && (
                            <Flex justify="end">
                                <Button
                                    size="small"
                                    icon={<ReloadOutlined />}
                                    loading={isRetryingExternalPush}
                                    onClick={onRetryExternalPush}
                                >
                                    {_t("orders.externalPush.retryPush")}
                                </Button>
                            </Flex>
                        )}
                    </Flex>
                )}
            </GlassCard>
        );
    }

    return (
        <GlassCard className="mb-6 mt-5 shadow-lg">
            <Flex gap={12} align="center" justify="space-between">
                <Flex gap={12} align="center">
                    <Avatar
                        size={48}
                        shape="square"
                        className="bg-gray-300 opacity-50"
                        icon={<ClockCircleOutlined className="text-2xl text-gray-500" />}
                    />
                    <Flex vertical>
                        <Typography.Text type="secondary">
                            {_t("orders.titles.unassigned")}
                        </Typography.Text>
                        {elapsedTime !== "00:00" && (
                            <Typography.Text type="secondary" className="text-xs">
                                {elapsedTime}
                            </Typography.Text>
                        )}
                    </Flex>
                </Flex>
                <Button type="primary" onClick={onOpenModal}>
                    {_t("orders.titles.assign")}
                </Button>
            </Flex>
        </GlassCard>
    );
};
